import { gql, useQuery } from '@apollo/client';
import {
  Alert,
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
} from '@apollo/orbit';
import classnames from 'classnames';
import React from 'react';

import { ErrorBoundary } from 'src/components/common/errorBoundary/ErrorBoundary';
import { Form as FormWrapper } from 'src/components/common/form/Form';
import { Loading } from 'src/components/common/loading/Loading';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import { GraphqlCodeBlock } from 'src/components/graphqlCodeBlock/GraphqlCodeBlock';
import {
  PQDetailsModalQuery,
  PQDetailsModalQueryVariables,
} from 'src/lib/graphqlTypes/types';
import { useLDFlag } from 'src/lib/launchDarkly';

export interface Props {
  graphId: string;
  listId: string;
  pqId: string;
  clientName: string | null;
  onClose: () => void;
  __test?: {
    error?: boolean;
  };
}

const pqDetailsModalQuery = gql<
  PQDetailsModalQuery,
  PQDetailsModalQueryVariables
>`
  query PQDetailsModalQuery(
    $graphId: ID!
    $listId: ID!
    $pqId: ID!
    $clientName: String
  ) {
    graph(id: $graphId) {
      id
      persistedQueryList(id: $listId) {
        id
        operation(id: $pqId, clientName: $clientName) {
          id
          clientName
          name
          body
        }
      }
    }
  }
`;

export const PersistedQueryDetailsModal = ({
  graphId,
  listId,
  pqId,
  clientName,
  onClose,
  __test,
}: Props) => {
  const {
    data: pqData,
    loading,
    error: pqError,
  } = useQuery(pqDetailsModalQuery, {
    variables: { graphId, listId, pqId, clientName },
  });
  const displayClientNames = useLDFlag(
    'persisted-queries-operation-client-name-display',
  );

  const pq = pqData?.graph?.persistedQueryList?.operation;
  const error = __test?.error || pqError || !pq;
  return (
    <Modal onClose={onClose} isOpen size="4xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader
          className={classnames({
            'border-t border-primary pb-0': !loading && !error,
          })}
        >
          <ModalTitle>Operation Details</ModalTitle>
          {!loading && !error ? (
            <ModalDescription>
              <p className="truncate">
                <em>Name: </em>
                <span className="text-secondary">{pq.name}</span>
              </p>
              <p className="truncate">
                <em>ID: </em>
                <span className="text-secondary">
                  <code>{pq.id}</code>
                </span>
              </p>
              {displayClientNames && (
                <p className="truncate">
                  <em>Client Name: </em>
                  <span className="text-secondary">
                    {pq.clientName ?? '(any)'}
                  </span>
                </p>
              )}
              <h3 className="-mx-6 mt-4 border-t border-primary px-6 py-2 text-base font-semibold">
                Document
              </h3>
            </ModalDescription>
          ) : null}
        </ModalHeader>
        <ModalBody className="p-0">
          {loading ? (
            <Loading />
          ) : error ? (
            <section className="px-6 py-2">
              <ErrorMessage />
            </section>
          ) : (
            <FormWrapper
              onSubmit={onClose}
              noValidate
              className="bg-silver-lighter px-6 py-2"
            >
              <ErrorBoundary
                // if we catch an error rendering the GraphQL codeblock due to parse failure,
                // fall back to a simple text renderer.
                renderError={() => (
                  <div className="p-8">
                    <Alert status="warning">
                      There was a problem parsing this operation signature.
                      Check for GraphQL syntax errors in the operation below, or
                      open in Explorer for additional formatting hints.
                    </Alert>
                    <div className="mt-4 font-mono text-sm">{pq.body}</div>
                  </div>
                )}
              >
                <GraphqlCodeBlock className="text-sm" sdl={pq.body} />
              </ErrorBoundary>
            </FormWrapper>
          )}
        </ModalBody>
        <ModalFooter
          className={classnames({
            'border-t border-primary': !loading && !error,
          })}
        >
          <Button onClick={onClose}>Close</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
